import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Image } from "react-bootstrap";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { User } from "../../types/Types";
import "../css/ProfileModal.css";
import { FaFlag, FaMapMarkerAlt } from "react-icons/fa";
import CurvedTextOverlay from "../Account/CurvedTextOverlay";
import BusinessLogoPreview from "../Account/BusinessLogoPreview";
import ReportUserModal from "./ReportUserModal";
import ProfileModalYouthDetails from "./ProfileModalYouthDetails";
import ProfileModalBusinessDetails from "./ProfileModalBusinessDetails";
import { IMAGE_SIZES, formatName } from "./profileModalHelpers";
import { useAppContext } from "../../Context/AppContext";
import { useMediaQuery } from "react-responsive";
import {
  normalizeMembership,
  getAvatarUrl,
  getProfileMembershipBadgeTheme,
} from "../../utils/connectionUserUtils";

interface ProfileModalProps {
  show: boolean;
  onHide: () => void;
  selectedUser: User | null;
  /** Falls back to translated default when omitted */
  title?: string;
  isConnected?: boolean;
}

function ProfileModal({
  show,
  onHide,
  selectedUser,
  title,
  isConnected,
}: ProfileModalProps) {
  const { t, i18n } = useTranslation();
  const { objectId, user: currentUser } = useAppContext();
  const [showReportModal, setShowReportModal] = useState(false);
  const displayTitle = title ?? t("profile.defaultTitle");
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLgTablet = useMediaQuery({ minWidth: 992, maxWidth: 1023 });
  const isMdTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });

  const imageSize = useMemo(
    () =>
      isDesktop
        ? IMAGE_SIZES.desktop
        : isLgTablet
          ? IMAGE_SIZES.lgTablet
          : isMdTablet
            ? IMAGE_SIZES.mdTablet
            : IMAGE_SIZES.mobile,
    [isDesktop, isLgTablet, isMdTablet],
  );

  const fullName = useMemo(() => {
    if (!selectedUser) return "";
    return `${formatName(selectedUser.firstName || "")} ${formatName(selectedUser.lastName || "")}`.trim();
  }, [selectedUser]);

  const canReportUser = useMemo(() => {
    if (!selectedUser) return false;
    const myObjectId = objectId ?? currentUser?.user?.objectId;
    const myId = currentUser?.user?.id;
    if (
      myObjectId &&
      selectedUser.objectId &&
      myObjectId === selectedUser.objectId
    ) {
      return false;
    }
    if (myId != null && selectedUser.id != null && myId === selectedUser.id) {
      return false;
    }
    return true;
  }, [
    currentUser?.user?.id,
    currentUser?.user?.objectId,
    objectId,
    selectedUser,
  ]);

  const handleOpenReport = useCallback(() => setShowReportModal(true), []);
  const handleCloseReport = useCallback(() => setShowReportModal(false), []);

  const handleHideProfile = useCallback(() => {
    setShowReportModal(false);
    onHide();
  }, [onHide]);

  useEffect(() => {
    if (!show) setShowReportModal(false);
  }, [show]);

  const membershipInfo = useMemo(() => {
    if (!selectedUser) return "";
    if (normalizeMembership(selectedUser.membershipName) === "Youth Membership") {
      return selectedUser.studyInterest
        ? formatName(selectedUser.studyInterest)
        : "";
    }
    return selectedUser.personalPositionCompany
      ? formatName(selectedUser.personalPositionCompany)
      : "";
  }, [selectedUser]);

  const profileSummaryFields = useMemo(() => {
    if (!selectedUser) return {};
    const fields: Record<string, string> = {};
    if (fullName) fields.fullName = fullName;
    if (membershipInfo) fields.membershipInfo = membershipInfo;
    const sector = selectedUser.businessSectorIndustry?.trim();
    if (sector) fields.sector = sector;
    const city = selectedUser.city?.trim();
    if (city) fields.city = city;
    const businessName = selectedUser.businessName?.trim();
    if (businessName) fields.businessName = businessName;
    const universityCollege = selectedUser.universityCollege?.trim();
    if (universityCollege) fields.universityCollege = universityCollege;
    return fields;
  }, [selectedUser, fullName, membershipInfo]);

  const { data: translatedSummary } = useTranslateDynamicFields(
    profileSummaryFields,
    i18n.resolvedLanguage || i18n.language,
  );

  const displayFullName = translatedSummary?.fullName?.trim() || fullName;
  const displayMembershipInfo =
    translatedSummary?.membershipInfo?.trim() || membershipInfo;
  const displaySector =
    translatedSummary?.sector?.trim() ||
    selectedUser?.businessSectorIndustry ||
    "";
  const displayCity =
    translatedSummary?.city?.trim() || selectedUser?.city || "";
  const displayBusinessName =
    translatedSummary?.businessName?.trim() || selectedUser?.businessName || "";
  const displayUniversityCollege =
    translatedSummary?.universityCollege?.trim() ||
    selectedUser?.universityCollege ||
    "";

  const profileImageUrl = useMemo(
    () => (selectedUser ? getAvatarUrl(selectedUser) : "") || "/user.png",
    [selectedUser],
  );

  const membershipBadgeTheme = useMemo(
    () =>
      getProfileMembershipBadgeTheme({
        membershipName: selectedUser?.membershipName ?? "",
        explorerAttendeeType: selectedUser?.explorerAttendeeType ?? "",
        hasPaid: selectedUser?.hasPaid,
        stripeStatus: selectedUser?.stripeStatus,
        userCategory: selectedUser?.userCategory,
      }),
    [
      selectedUser?.membershipName,
      selectedUser?.explorerAttendeeType,
      selectedUser?.hasPaid,
      selectedUser?.stripeStatus,
      selectedUser?.userCategory,
    ],
  );

  const bannerStyle = useMemo(
    () => ({
      backgroundImage: selectedUser?.BannerImage
        ? `url(${selectedUser.BannerImage})`
        : undefined,
      backgroundSize: "cover" as const,
      backgroundPosition: "center" as const,
      backgroundRepeat: "no-repeat" as const,
    }),
    [selectedUser?.BannerImage],
  );

  const imageContainerStyle = useMemo(
    () => ({ width: imageSize, height: imageSize }),
    [imageSize],
  );

  const isYouthMembership =
    !!selectedUser &&
    normalizeMembership(selectedUser.membershipName) === "Youth Membership";

  const businessLogoSrc =
    selectedUser?.BusinessLogo || selectedUser?.businessLogo || "";

  return (
    <>
      <Modal
        size="lg"
        show={show && !showReportModal}
        onHide={handleHideProfile}
        centered
        className="profile-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">{displayTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {selectedUser && (
            <>
              <div className="profile-modal-card rounded-3 overflow-hidden border bg-white">
                <div
                  className={`profile-banner position-relative d-flex justify-content-center align-items-center ${
                    selectedUser.BannerImage ? "" : "bg-project-gradient"
                  }`}
                  style={bannerStyle}
                >
                  {!selectedUser.BannerImage && (
                    <img
                      src="/SMBF-logo-white.png"
                      alt=""
                      width={96}
                      height={96}
                      className="profile-banner-logo opacity-75"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                </div>

                <div className="px-3 px-md-4 pb-3">
                  <div className="profile-hero d-flex flex-column flex-md-row align-items-start gap-3">
                    <div
                      className="position-relative flex-shrink-0 profile-avatar-wrap"
                      style={imageContainerStyle}
                    >
                      <Image
                        src={profileImageUrl}
                        roundedCircle
                        alt={t("profile.alt.user")}
                        width={imageSize}
                        height={imageSize}
                        className="profile-image w-100 h-100 shadow-sm border border-2 border-white"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          const img = e.target as HTMLImageElement;
                          if (img.src && !img.src.endsWith("/user.png")) {
                            img.src = "/user.png";
                          }
                        }}
                      />
                      {membershipBadgeTheme ? (
                        <span className="badge-overlay text-white fw-bold px-2 py-1 rounded-pill">
                          <CurvedTextOverlay
                            size={imageSize}
                            stroke={membershipBadgeTheme.stroke}
                            text={membershipBadgeTheme.text}
                          />
                        </span>
                      ) : null}
                    </div>

                    <div className="profile-summary-row min-w-0 flex-grow-1 w-100">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start gap-3">
                        <div className="profile-summary-primary min-w-0">
                          <h2 className="h4 fw-bold mb-1 text-dark text-truncate">
                            {displayFullName}
                          </h2>
                          {displayMembershipInfo ? (
                            <div className="text-muted mb-1 text-truncate">
                              {displayMembershipInfo}
                            </div>
                          ) : null}
                          {displaySector ? (
                            <div className="small text-muted text-truncate">
                              {displaySector}
                            </div>
                          ) : null}
                          {!isConnected && displayCity ? (
                            <div className="d-flex gap-1 text-muted align-items-center small mt-1">
                              <FaMapMarkerAlt aria-hidden />
                              <span className="text-truncate">{displayCity}</span>
                            </div>
                          ) : null}
                        </div>

                        {!isYouthMembership ? (
                          <div className="d-flex align-items-center gap-2 flex-shrink-0 profile-summary-secondary">
                            <BusinessLogoPreview
                              src={businessLogoSrc}
                              name={displayBusinessName}
                              size={56}
                              alt={
                                displayBusinessName
                                  ? t("profile.alt.businessLogoFor", {
                                      name: displayBusinessName,
                                    })
                                  : t("profile.alt.businessLogo")
                              }
                            />
                            <div className="fw-semibold text-truncate" style={{ maxWidth: 160 }}>
                              {displayBusinessName || t("profile.notProvidedYet")}
                            </div>
                          </div>
                        ) : displayUniversityCollege ? (
                          <div className="fw-semibold text-muted small text-md-end">
                            {displayUniversityCollege}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>

                {isYouthMembership ? (
                  <ProfileModalYouthDetails
                    selectedUser={selectedUser}
                    isConnected={isConnected}
                  />
                ) : (
                  <ProfileModalBusinessDetails
                    selectedUser={selectedUser}
                    isConnected={isConnected}
                  />
                )}
              </div>

              {canReportUser ? (
                <div className="d-flex justify-content-end pt-3">
                  <button
                    type="button"
                    className="btn btn-link text-secondary text-decoration-none p-0 small fw-medium d-inline-flex align-items-center gap-2"
                    onClick={handleOpenReport}
                  >
                    <FaFlag className="opacity-75" aria-hidden />
                    {t("profile.report.link")}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </Modal.Body>
      </Modal>
      <ReportUserModal
        show={show && showReportModal}
        onHide={handleCloseReport}
        reportedUser={selectedUser}
      />
    </>
  );
}

ProfileModal.displayName = "ProfileModal";

export default memo(ProfileModal);
